import { GitBranch } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, Navigate } from 'react-router'

import { isProduction } from '@/config/config'
import { useCurrentUser } from '@/models/user'
import { Cap } from '../../primitives'
import { GraphCanvas } from './GraphCanvas'
import { buildGraph } from './layout'
import { StepDrawer } from './StepDrawer'
import { STAGES } from './tree'

/**
 * The insurance application's question flow as a left-to-right graph, on its own
 * full-width page — 40 columns of it don't fit the design-system page's measure.
 * Layout is derived from the flow data, so adding a step in `tree.ts` re-lays out.
 */
export function ConversationTreePage() {
  const { user } = useCurrentUser({ enabled: isProduction })
  const graph = useMemo(() => buildGraph(), [])
  const [selected, setSelected] = useState<string | null>(null)

  // Self-loops ride on their node rather than as drawn edges, so add them back.
  const branchCount = graph.edges.length + graph.nodes.reduce((sum, n) => sum + n.selfLoops.length, 0)
  const node = graph.nodes.find((n) => n.service === selected)
  const outgoing = graph.edges.filter((e) => e.from === selected)
  const incoming = graph.edges.filter((e) => e.to === selected)

  if (isProduction && !user?.isCaiAdmin) {
    return <Navigate to="/insure" replace />
  }

  return (
    // pointer-events-auto undoes the `none` vaul puts on body while the drawer is open.
    <div className="bg-ink-50 pointer-events-auto flex h-dvh flex-col overflow-hidden">
      <Helmet>
        <title>Application conversation tree</title>
      </Helmet>

      <div className="bg-ink-900 text-ink-50 shrink-0">
        <div className="flex items-center gap-2 px-4 py-3 sm:px-6">
          <GitBranch className="size-4 shrink-0" />
          <span className="font-mono text-xs tracking-[0.08em] uppercase">Application conversation tree</span>
          <span className="text-ink-400 font-mono text-[10px]">
            {STAGES.length} stages · {graph.nodes.length - 1} questions · {branchCount} branches
          </span>
          <Link
            to="/design-system"
            className="text-ink-300 hover:text-ink-50 ml-auto shrink-0 text-xs underline underline-offset-2"
          >
            Back to Design System
          </Link>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 px-4 py-4 sm:px-6">
        <LegendLine className="stroke-ink-300" label="Next question" />
        <LegendLine className="stroke-brand-600/60" label="Skips ahead — arcs over" />
        <LegendLine className="stroke-ink-400" dashed label="Goes back — loops under" />
        <div className="flex items-center gap-2">
          <span className="border-ink-200 bg-surface text-ink rounded-full border px-2 py-0.5 text-[10px]">
            Answer
          </span>
          <span className="border-ink-100 bg-ink-50 text-ink-500 rounded-full border px-2 py-0.5 text-[10px] italic">
            What comes back
          </span>
        </div>
        <Cap>Click a question for detail</Cap>
      </div>

      {/* Fills the rest of the viewport, so its horizontal scrollbar sits on the bottom edge. */}
      <div className="border-ink-100 min-h-0 flex-1 overflow-auto border-t">
        <GraphCanvas graph={graph} selected={selected} onSelect={setSelected} />
      </div>

      <StepDrawer
        node={node}
        incoming={incoming}
        outgoing={outgoing}
        onSelect={setSelected}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

/** One rule from the edge key. */
function LegendLine({ className, dashed, label }: { className: string; dashed?: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <svg width="34" height="8" aria-hidden>
        <line
          x1="0"
          y1="4"
          x2="34"
          y2="4"
          className={className}
          strokeWidth="1.5"
          strokeDasharray={dashed ? '4 3' : undefined}
        />
      </svg>
      <Cap>{label}</Cap>
    </div>
  )
}
